import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from './Button'

export interface PaginationProps {
  currentPage: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  hasNextPage?: boolean
  hasPrevPage?: boolean
  isLoading?: boolean
  className?: string
}

/**
 * Mobile-first pagination component with compact format.
 * Shows "1-20 of 100" format instead of verbose text.
 */
export function Pagination({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  hasNextPage = true,
  hasPrevPage = true,
  isLoading = false,
  className = '',
}: PaginationProps) {
  const startItem = ((currentPage - 1) * itemsPerPage) + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  const handlePrevious = () => {
    if (hasPrevPage && !isLoading) {
      onPageChange(Math.max(1, currentPage - 1))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNext = () => {
    if (hasNextPage && !isLoading) {
      onPageChange(currentPage + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Don't show pagination if only one page
  if (totalItems <= itemsPerPage) {
    return null
  }

  return (
    <div className={`flex items-center justify-between border-t border-gray-200 pt-4 ${className}`}>
      {/* Compact count - mobile friendly */}
      <div className="text-sm text-gray-600">
        <span className="font-medium">{startItem}-{endItem}</span>
        {' '}of{' '}
        <span className="font-medium">{totalItems}</span>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!hasPrevPage || isLoading}
          onClick={handlePrevious}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">Prev</span>
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!hasNextPage || isLoading}
          onClick={handleNext}
          aria-label="Next page"
        >
          <span className="hidden sm:inline">Next</span>
          <span className="sm:hidden">Next</span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  )
}
